import { computed, onMounted, ref, watch, type Ref } from 'vue';
import { usePermission } from '@/entities/Permission';
import { useUserStore } from '@/entities/User/@x/Session';
import { useCustomToast, useListUrlSync } from '@/shared/lib';
import type { RecordSelection } from '@/features/Record';
import type { DocumentRevision } from '@/entities/Document';
import {
  bulkUpdateDirectoryPermissions,
  createFolder,
  createModule,
  deleteFolder,
  deleteModule,
  FOLDER_ALREADY_EXISTS_MESSAGE,
  getDirectoryPermissions,
  getModulesWithFolders,
  MODULE_ALREADY_EXISTS_MESSAGE,
  moveDirectoryItems,
  removeDirectoryItems,
  updateDirectoryPositionPermissions,
  updateDirectoryFunctionalActivityPermissions,
  updateDirectoryMatrixArchive,
  updateFolder,
  updateModule,
  type BulkDirectoryPermissionSections,
  type DirectoryPermissionsTarget,
  type UpdateDirectoryPermissionsResult,
  type ModuleTreeSortField,
  type ModuleTreeSortOrder,
  type MoveDestination,
  type MoveItem,
  type RemoveDirectoryItem,
  type UpdateDirectoryNodeResult,
} from '../api/directories.api';
import { linkMatricesToContainer } from '../api/matrices.api';
import { linkDocumentRevisionsToContainer } from '../api/documents.api';
import { DIRECTORY_NODE_COPY } from '../config/directoryNodeCopy';
import { isDirectoryTitleValid } from '../config/directoryTitle';
import {
  ADD_DOCUMENT_DUPLICATE_MESSAGE,
  ADD_DOCUMENT_ERROR_MESSAGE,
  ADD_DOCUMENT_SUCCESS_MESSAGE,
  ADD_RECORD_ERROR_MESSAGE,
  ADD_RECORD_SUCCESS_MESSAGE,
  CREATE_FOLDER_ERROR_MESSAGE,
  CREATE_FOLDER_SUCCESS_MESSAGE,
  CREATE_MODULE_ERROR_MESSAGE,
  CREATE_MODULE_SUCCESS_MESSAGE,
  COPY_PERMISSIONS_SUCCESS_MESSAGE,
  buildDeleteConfirmationTitle,
  buildDeleteErrorMessage,
  buildDeleteSuccessMessage,
  DELETE_SELECTION_CONFIRMATION_DESCRIPTION,
  type DeleteMessageItemType,
  LOAD_MODULES_ERROR_MESSAGE,
  LOAD_PERMISSIONS_ERROR_MESSAGE,
  MOVE_ITEM_ERROR_MESSAGE,
  MOVE_ITEM_SUCCESS_MESSAGE,
  MOVE_ITEMS_SUCCESS_MESSAGE,
  SAVE_CONFIGURE_ERROR_MESSAGE,
  SAVE_CONFIGURE_SUCCESS_MESSAGE,
  SAVE_PERMISSIONS_ERROR_MESSAGE,
  SAVE_PERMISSIONS_SUCCESS_MESSAGE,
} from '../config/messages';
import type { CreateModuleSubmitPayload } from './useCreateModuleForm';
import type { CreateFolderSubmitPayload } from './useCreateFolderForm';
import {
  appendChildToTree,
  extractNumericId,
  findNodeById,
  hasDuplicateSiblingTitle,
  moveNodesToParent,
  normalizeMoveNodeIds,
  removeNodeById,
  updateNodeById,
} from './directoryTree.utils';
import { resolveSelectionDeleteLockMessage } from './directoryDeleteLock';
import { DEFAULT_DIRECTORY_ICON_ID } from '@/shared/ui/module-icons';
import { useDirectoryDragAndDrop } from './useDirectoryDragAndDrop';
import { mapCreatedModuleToTreeNode } from './mapCreatedModuleToTreeNode';
import { mapModuleWithFoldersToTreeNode } from './mapModuleWithFoldersToTreeNode';
import { mapCreatedFolderToTreeNode } from './mapCreatedFolderToTreeNode';
import { mapDocumentRevisionToTreeNode } from './mapDocumentRevisionToTreeNode';
import {
  buildEligibleDocumentsSearchUrl,
  isDocumentAlreadyLinkedError,
  resolveDocumentLinkContainer,
} from './directoryDocumentLink';
import {
  buildEligibleMatricesSearchUrl,
  resolveMatrixLinkErrorToast,
  resolveRecordLinkContainer,
} from './directoryRecordLink';
import { attachMockChildrenToModule } from './attachMockChildrenToModule';
import { collectExpandedIdsForSearch, filterDirectoryTree } from './filterDirectoryTree';
import type { DirectorySearchCriterion } from './directorySearch.utils';
import { DEFAULT_EXPANDED_DIRECTORY_IDS } from './mockDirectoriesTree';
import { resolveDirectoryPermissionAccess } from './directoryPermissionAccess';
import { useDirectoryPermissionsClipboardStore } from './permissionsClipboard.store';
import type {
  DirectoryActionsPanelTab,
  DirectoryConfigureDraft,
  DirectoryNodePermissions,
  DirectoryPermissionSectionKey,
  DirectoryPermissionTargetType,
  DirectoryTreeNode,
} from './types';

interface CreateFolderTarget {
  moduleId: number;
  parentId: number | null;
}

function buildCreateFolderTarget(node: DirectoryTreeNode): CreateFolderTarget | null {
  if (node.kind === 'module') {
    const moduleId = extractNumericId(node.id, 'module-');
    if (moduleId === undefined) {
      return null;
    }
    return { moduleId, parentId: null };
  }
  if (node.kind === 'folder') {
    if (node.moduleId === undefined) {
      return null;
    }
    const parentId = extractNumericId(node.id, 'folder-');
    return { moduleId: node.moduleId, parentId: parentId ?? null };
  }
  return null;
}

const KIND_TO_ITEM_TYPE: Partial<Record<DirectoryTreeNode['kind'], MoveItem['itemType']>> = {
  document: 'DOCUMENT',
  record: 'RECORD',
  matrix: 'MATRIX',
};

function buildMoveItem(tree: DirectoryTreeNode[], nodeId: string): MoveItem | null {
  const node = findNodeById(tree, nodeId);
  if (!node) {
    return null;
  }

  if (node.kind === 'folder') {
    const itemId = extractNumericId(nodeId, 'folder-');
    if (itemId === undefined) {
      return null;
    }
    return { itemType: 'FOLDER', itemId };
  }

  const itemType = KIND_TO_ITEM_TYPE[node.kind];
  if (!itemType || node.itemId === undefined || node.derived === true) {
    return null;
  }
  return { itemType, itemId: node.itemId, linkId: node.linkId ?? undefined };
}

function buildRemoveDirectoryItem(node: DirectoryTreeNode): RemoveDirectoryItem | null {
  const itemType = KIND_TO_ITEM_TYPE[node.kind];
  if (!itemType || itemType === 'FOLDER' || node.itemId === undefined || node.derived === true) {
    return null;
  }
  return { itemType, itemId: node.itemId, linkId: node.linkId ?? undefined };
}

export type DirectoryDeleteSelectionState = 'none' | 'single' | 'bulk' | 'mixed';

const DELETABLE_LEAF_ITEM_KINDS = new Set<DirectoryTreeNode['kind']>([
  'document',
  'record',
  'matrix',
]);

const LISTING_ONLY_KINDS = new Set<DirectoryTreeNode['kind']>(['generatedRecord']);

function resolveDeleteSelectionState(
  tree: DirectoryTreeNode[],
  nodeIds: string[]
): DirectoryDeleteSelectionState {
  if (nodeIds.length === 0) {
    return 'none';
  }
  const nodes = nodeIds.map((nodeId) => findNodeById(tree, nodeId));
  if (nodes.some((node) => node !== null && LISTING_ONLY_KINDS.has(node.kind))) {
    return 'none';
  }
  if (nodeIds.length === 1) {
    return 'single';
  }
  if (nodes.some((node) => !node)) {
    return 'mixed';
  }
  const leafKinds = nodes.map((node) => node?.kind);
  const isLeafOnlySelection = leafKinds.every(
    (kind) => kind !== undefined && DELETABLE_LEAF_ITEM_KINDS.has(kind)
  );
  return isLeafOnlySelection ? 'bulk' : 'mixed';
}

const LEAF_KIND_TO_MESSAGE_ITEM_TYPE: Partial<
  Record<DirectoryTreeNode['kind'], DeleteMessageItemType>
> = {
  document: 'document',
  matrix: 'matrix',
  record: 'record',
};

function resolveDeleteMessageItemType(nodes: DirectoryTreeNode[]): DeleteMessageItemType {
  if (nodes.length === 1) {
    const [node] = nodes;
    if (node?.kind === 'module') {
      return 'module';
    }
    if (node?.kind === 'folder') {
      return 'folder';
    }
    return LEAF_KIND_TO_MESSAGE_ITEM_TYPE[node?.kind ?? 'document'] ?? 'document';
  }

  const isEveryNodeRecord = nodes.every((node) => node.kind === 'record');
  if (isEveryNodeRecord) {
    return 'record';
  }

  const isLeafOnlySelection = nodes.every((node) => DELETABLE_LEAF_ITEM_KINDS.has(node.kind));
  return isLeafOnlySelection ? 'items' : 'document';
}

function buildDeleteConfirmationContent(itemType: DeleteMessageItemType): {
  title: string;
  description: string;
} {
  return {
    title: buildDeleteConfirmationTitle(itemType),
    description: DELETE_SELECTION_CONFIRMATION_DESCRIPTION,
  };
}

function buildMoveDestination(targetId: string): MoveDestination | null {
  const moduleId = extractNumericId(targetId, 'module-');
  if (moduleId !== undefined) {
    return { moduleId };
  }
  const folderId = extractNumericId(targetId, 'folder-');
  if (folderId !== undefined) {
    return { folderId };
  }
  return null;
}

function buildConfigureDraft(node: DirectoryTreeNode): DirectoryConfigureDraft {
  return {
    title: node.details?.title ?? node.name,
    iconId: DIRECTORY_NODE_COPY[node.kind].showIconField
      ? (node.details?.iconId ?? DEFAULT_DIRECTORY_ICON_ID)
      : node.details?.iconId,
    archived: DIRECTORY_NODE_COPY[node.kind].showArchiveField
      ? node.details?.archived === true
      : undefined,
  };
}

function clonePermissions(permissions: DirectoryNodePermissions): DirectoryNodePermissions {
  return {
    superadmin: permissions.superadmin,
    positions: permissions.positions.map((entry) => ({ ...entry })),
    functionalActivities: permissions.functionalActivities.map((entry) => ({ ...entry })),
  };
}

function getSectionEntries(
  permissions: DirectoryNodePermissions,
  section: DirectoryPermissionSectionKey
) {
  return section === 'positions' ? permissions.positions : permissions.functionalActivities;
}

function updateSectionEntries(
  permissions: DirectoryNodePermissions,
  section: DirectoryPermissionSectionKey,
  entries: DirectoryNodePermissions['positions']
): DirectoryNodePermissions {
  if (section === 'positions') {
    return { ...permissions, positions: entries };
  }
  return { ...permissions, functionalActivities: entries };
}

const DIRECTORY_SEARCH_CRITERIA: DirectorySearchCriterion[] = [
  'module',
  'folder',
  'title',
  'prefix',
];

function isModuleTreeSortField(value: string): value is ModuleTreeSortField {
  return value === 'name' || value === 'createdAt';
}

function isModuleTreeSortOrder(value: string): value is ModuleTreeSortOrder {
  return value === 'asc' || value === 'desc';
}

export interface UseDirectoriesPageOptions {
  initialTree?: DirectoryTreeNode[];
}

export function useDirectoriesPage(options?: UseDirectoriesPageOptions) {
  const { can } = usePermission();
  const toast = useCustomToast();
  const permissionsClipboard = useDirectoryPermissionsClipboardStore();
  const initialTree = options?.initialTree;
  const usesProvidedTree = initialTree !== undefined;
  const userStore = useUserStore();

  const {
    searchTerm,
    searchFieldId: searchCriterionRef,
    sort,
    order,
    syncUrl,
  } = useListUrlSync({
    fields: DIRECTORY_SEARCH_CRITERIA,
    defaultField: 'module',
    fieldQueryKey: 'searchCriterion',
    validSorts: ['name', 'createdAt'],
  });
  const searchCriterion = searchCriterionRef as unknown as Ref<DirectorySearchCriterion>;
  const expandedIds = ref<string[]>([...DEFAULT_EXPANDED_DIRECTORY_IDS]);
  const selectedNodeId = ref<string>('');
  const selectedNodeIds = ref<string[]>([]);
  const actionsPanelTab = ref<DirectoryActionsPanelTab>('configure');
  const draftDetails = ref<DirectoryConfigureDraft | null>(null);
  const savedPermissions = ref<DirectoryNodePermissions | null>(null);
  const draftPermissions = ref<DirectoryNodePermissions | null>(null);
  const isSavingConfigure = ref(false);
  const isSavingPositions = ref(false);
  const isSavingFunctionalActivities = ref(false);
  const isLoadingPermissions = ref(false);
  const serverTitleError = ref('');
  const isPastingPermissions = ref(false);
  const directoryTree = ref<DirectoryTreeNode[]>(
    usesProvidedTree ? (structuredClone(initialTree) as DirectoryTreeNode[]) : []
  );
  const isCreateModuleSubmitting = ref(false);
  const createModuleTitleError = ref('');
  const isCreateFolderSubmitting = ref(false);
  const createFolderTitleError = ref('');
  const isLoadingModules = ref(false);
  const isAddRecordSubmitting = ref(false);
  const isAddDocumentSubmitting = ref(false);
  const isDeletingSelection = ref(false);
  let loadModulesRequestId = 0;
  let loadPermissionsRequestId = 0;

  const canCreateModule = can('createModule', 'Directory');
  const canEditPermissions = can('editPermissions', 'Directory');
  const canManagePermissions = computed(() => userStore.isSuperuser || canEditPermissions.value);
  const canReadDirectory = can('read', 'Directory');
  const hasModules = computed(() => directoryTree.value.length > 0);

  const deleteSelectionState = computed(() =>
    resolveDeleteSelectionState(directoryTree.value, selectedNodeIds.value)
  );
  const selectedCount = computed(() => selectedNodeIds.value.length);
  const isDeleteButtonVisible = computed(
    () =>
      canReadDirectory.value &&
      (deleteSelectionState.value === 'single' || deleteSelectionState.value === 'bulk')
  );
  const selectedNodesForDeletion = computed(() =>
    selectedNodeIds.value
      .map((nodeId) => findNodeById(directoryTree.value, nodeId))
      .filter((node): node is DirectoryTreeNode => node !== null)
  );
  const deleteLockMessage = computed(() =>
    resolveSelectionDeleteLockMessage(selectedNodesForDeletion.value)
  );
  const deleteMessageItemType = computed(() =>
    resolveDeleteMessageItemType(selectedNodesForDeletion.value)
  );
  const deleteConfirmationContent = computed(() =>
    buildDeleteConfirmationContent(deleteMessageItemType.value)
  );
  const deleteModalTitle = computed(() => deleteConfirmationContent.value.title);
  const deleteModalDescription = computed(() => deleteConfirmationContent.value.description);

  const filteredTree = computed(() =>
    filterDirectoryTree(directoryTree.value, searchTerm.value, searchCriterion.value)
  );

  const currentSort = computed(() => sort.value);
  const currentOrder = computed(() => order.value);

  const shouldShowSearchNoResults = computed(
    () => hasModules.value && searchTerm.value.trim() !== '' && filteredTree.value.length === 0
  );

  const selectedNode = computed(() => {
    if (!selectedNodeId.value) {
      return null;
    }
    return findNodeById(directoryTree.value, selectedNodeId.value);
  });

  const permissionsNodeId = computed(
    () => selectedNodeIds.value[selectedNodeIds.value.length - 1] ?? ''
  );

  const permissionsNode = computed(() => {
    if (!permissionsNodeId.value) {
      return null;
    }
    return findNodeById(directoryTree.value, permissionsNodeId.value);
  });

  const selectedNodeDetails = computed(() => {
    if (!selectedNode.value) {
      return null;
    }
    const draft = draftDetails.value;
    const base = selectedNode.value.details;
    return {
      title: draft?.title ?? base?.title ?? selectedNode.value.name,
      iconId: draft?.iconId ?? base?.iconId,
      archived: draft?.archived ?? base?.archived,
      createdAtLabel: base?.createdAtLabel ?? '-',
      createdBy: base?.createdBy ?? '-',
      savedInDirectoryBy: base?.savedInDirectoryBy ?? '-',
    };
  });

  const configureCopy = computed(() =>
    selectedNode.value ? DIRECTORY_NODE_COPY[selectedNode.value.kind] : null
  );

  const isConfigureDirty = computed(() => {
    const node = selectedNode.value;
    const draft = draftDetails.value;
    const copy = configureCopy.value;
    if (!node || !draft || !copy) {
      return false;
    }
    const baseline = buildConfigureDraft(node);
    if (copy.showTitleField && draft.title.trim() !== baseline.title.trim()) {
      return true;
    }
    if (copy.showIconField && draft.iconId !== baseline.iconId) {
      return true;
    }
    return copy.showArchiveField && draft.archived !== baseline.archived;
  });

  const isConfigureTitleValid = computed(() => {
    const copy = configureCopy.value;
    if (!copy?.showTitleField) {
      return true;
    }
    return isDirectoryTitleValid(draftDetails.value?.title ?? '');
  });

  const isConfigureIconValid = computed(() => {
    const copy = configureCopy.value;
    if (!copy?.showIconField) {
      return true;
    }
    return Boolean(draftDetails.value?.iconId);
  });

  const configureTitleError = computed(() => {
    const node = selectedNode.value;
    const draft = draftDetails.value;
    const copy = configureCopy.value;
    if (!node || !draft || !copy?.showTitleField) {
      return '';
    }
    if (serverTitleError.value) {
      return serverTitleError.value;
    }
    if (!isDirectoryTitleValid(draft.title)) {
      return '';
    }
    if (!hasDuplicateSiblingTitle(directoryTree.value, node.id, draft.title, node.kind)) {
      return '';
    }
    return copy.duplicateTitleMessage ?? '';
  });

  const canSaveConfigure = computed(
    () =>
      isConfigureDirty.value &&
      isConfigureTitleValid.value &&
      isConfigureIconValid.value &&
      configureTitleError.value === '' &&
      !isSavingConfigure.value
  );

  const selectedNodePermissions = computed(() => draftPermissions.value);

  const getAllowedIds = (
    permissions: DirectoryNodePermissions,
    section: DirectoryPermissionSectionKey
  ) =>
    getSectionEntries(permissions, section)
      .filter((entry) => entry.allowed)
      .map((entry) => entry.id)
      .sort((first, second) => first.localeCompare(second));

  const hasSectionChanges = (section: DirectoryPermissionSectionKey) => {
    if (!draftPermissions.value || !savedPermissions.value) {
      return false;
    }

    const draftIds = getAllowedIds(draftPermissions.value, section);
    const savedIds = getAllowedIds(savedPermissions.value, section);

    return (
      draftIds.length !== savedIds.length || draftIds.some((id, index) => id !== savedIds[index])
    );
  };

  const hasSectionSelection = (section: DirectoryPermissionSectionKey) =>
    draftPermissions.value
      ? getSectionEntries(draftPermissions.value, section).some((entry) => entry.allowed)
      : false;

  const canClearPositions = computed(() => hasSectionSelection('positions'));

  const canApplyPositions = computed(
    () => !isSavingPositions.value && hasSectionChanges('positions')
  );

  const canClearFunctionalActivities = computed(() => hasSectionSelection('functionalActivities'));

  const canApplyFunctionalActivities = computed(
    () => !isSavingFunctionalActivities.value && hasSectionChanges('functionalActivities')
  );

  const resolvePermissionsTarget = (node: DirectoryTreeNode): DirectoryPermissionsTarget | null => {
    const resolveTarget = (
      targetType: DirectoryPermissionTargetType,
      targetId: number | undefined
    ): DirectoryPermissionsTarget | null => {
      return targetId === undefined ? null : { targetType, targetId };
    };

    if (node.kind === 'module') {
      return resolveTarget('MODULE', extractNumericId(node.id, 'module-'));
    }

    if (node.kind === 'folder') {
      return resolveTarget('FOLDER', extractNumericId(node.id, 'folder-'));
    }

    return resolveTarget('ITEM', node.linkId ?? undefined);
  };

  const loadNodePermissions = async (node: DirectoryTreeNode) => {
    const requestId = ++loadPermissionsRequestId;

    if (!canManagePermissions.value) {
      savedPermissions.value = null;
      draftPermissions.value = null;
      return;
    }

    const target = resolvePermissionsTarget(node);
    const isStaleRequest = () =>
      requestId !== loadPermissionsRequestId || permissionsNodeId.value !== node.id;

    if (!target) {
      if (isStaleRequest()) {
        return;
      }

      const localPermissions = node.permissions ? clonePermissions(node.permissions) : null;
      savedPermissions.value = localPermissions;
      draftPermissions.value = localPermissions ? clonePermissions(localPermissions) : null;
      return;
    }

    isLoadingPermissions.value = true;
    try {
      const result = await getDirectoryPermissions(target);
      if (isStaleRequest()) {
        return;
      }

      if (!result.success) {
        savedPermissions.value = null;
        draftPermissions.value = null;
        toast.showErrorToast({ title: LOAD_PERMISSIONS_ERROR_MESSAGE, type: 'error' });
        return;
      }

      savedPermissions.value = clonePermissions(result.data);
      draftPermissions.value = clonePermissions(result.data);
      directoryTree.value = updateNodeById(directoryTree.value, node.id, (currentNode) => ({
        ...currentNode,
        permissions: clonePermissions(result.data),
      }));
    } finally {
      if (requestId === loadPermissionsRequestId) {
        isLoadingPermissions.value = false;
      }
    }
  };

  const eligibleDocumentsSearchUrl = computed(() =>
    buildEligibleDocumentsSearchUrl(selectedNode.value)
  );

  const eligibleMatricesSearchUrl = computed(() =>
    buildEligibleMatricesSearchUrl(selectedNode.value)
  );

  const directoryPermissionAccess = computed(() =>
    resolveDirectoryPermissionAccess({
      isSuperuser: userStore.isSuperuser,
      hasEditPermission: canEditPermissions.value,
      nodeKind: permissionsNode.value?.kind ?? null,
      nodeSuperadminRestricted: draftPermissions.value?.superadmin === true,
    })
  );
  const canViewPermissionsTab = computed(
    () => directoryPermissionAccess.value.canViewPermissionsTab
  );
  const canToggleSuperadmin = computed(() => directoryPermissionAccess.value.canToggleSuperadmin);

  watch(searchCriterion, () => {
    searchTerm.value = '';
    syncUrl();
  });

  watch(searchTerm, (term) => {
    if (term.trim()) {
      expandedIds.value = collectExpandedIdsForSearch(filteredTree.value);
    }
  });

  watch(
    selectedNodeId,
    (nodeId) => {
      serverTitleError.value = '';
      const node = nodeId ? findNodeById(directoryTree.value, nodeId) : null;
      draftDetails.value = node ? buildConfigureDraft(node) : null;
    },
    { immediate: true }
  );

  watch(
    permissionsNodeId,
    (nodeId) => {
      if (selectedNodeIds.value.length <= 1) {
        actionsPanelTab.value = 'configure';
      }
      const node = nodeId ? findNodeById(directoryTree.value, nodeId) : null;
      if (!node) {
        savedPermissions.value = null;
        draftPermissions.value = null;
        return;
      }
      void loadNodePermissions(node);
    },
    { immediate: true }
  );

  const getDirectoryKey = (node: DirectoryTreeNode) => node.id;

  const getDirectoryChildren = (node: DirectoryTreeNode) => node.children;

  const selectNode = (id: string, multiSelect = false) => {
    if (multiSelect) {
      const next = selectedNodeIds.value.includes(id)
        ? selectedNodeIds.value.filter((entry) => entry !== id)
        : [...selectedNodeIds.value, id];
      selectedNodeIds.value = next;
      selectedNodeId.value = next.length === 1 ? (next[0] ?? '') : '';
      return;
    }
    selectedNodeIds.value = [id];
    selectedNodeId.value = id;
  };

  const moveSelectedNodes = async (itemIds: string[], targetId: string) => {
    const normalizedItemIds = normalizeMoveNodeIds(directoryTree.value, itemIds);
    const items = normalizedItemIds
      .map((id) => buildMoveItem(directoryTree.value, id))
      .filter((item): item is MoveItem => item !== null);
    const destination = buildMoveDestination(targetId);

    if (items.length === 0 || !destination) {
      return;
    }

    // Moving a folder that carries a matrix link changes the placement locks of its old and new
    // ancestors, which only the server computes.
    const movesMatrixCarrier = normalizedItemIds.some(
      (id) => findNodeById(directoryTree.value, id)?.matrixPlacementDeleteLock !== undefined
    );
    const treeSnapshot = directoryTree.value;
    const selectedNodeIdSnapshot = selectedNodeId.value;
    const selectedNodeIdsSnapshot = selectedNodeIds.value;
    const savedInDirectoryBy = userStore.name ?? '-';
    let nextTree = moveNodesToParent(directoryTree.value, normalizedItemIds, targetId);
    for (const id of normalizedItemIds) {
      nextTree = updateNodeById(nextTree, id, (node) =>
        node.details ? { ...node, details: { ...node.details, savedInDirectoryBy } } : node
      );
    }
    directoryTree.value = nextTree;
    selectedNodeId.value = '';
    selectedNodeIds.value = [];

    const result = await moveDirectoryItems({ items, destination });
    if (result.success) {
      toast.showSuccessToast({
        title: items.length > 1 ? MOVE_ITEMS_SUCCESS_MESSAGE : MOVE_ITEM_SUCCESS_MESSAGE,
        type: 'success',
      });
      if (movesMatrixCarrier && !usesProvidedTree) {
        await loadModules();
      }
      return;
    }

    directoryTree.value = treeSnapshot;
    selectedNodeId.value = selectedNodeIdSnapshot;
    selectedNodeIds.value = selectedNodeIdsSnapshot;
    toast.showErrorToast({ title: result.message ?? MOVE_ITEM_ERROR_MESSAGE, type: 'error' });
  };

  const dragAndDrop = useDirectoryDragAndDrop({
    tree: directoryTree,
    selectedNodeIds,
    onMove: moveSelectedNodes,
  });

  const handleSearch = (term: string) => {
    searchTerm.value = term;
    if (term.trim()) {
      selectedNodeId.value = '';
      selectedNodeIds.value = [];
    }
    syncUrl();
  };

  const handleSortUpdate = (sortField: string, orderFlow: string) => {
    order.value = orderFlow === 'asc' || orderFlow === 'desc' ? orderFlow : '';
    sort.value = order.value ? sortField : '';
    syncUrl();
    if (!usesProvidedTree) {
      void loadModules();
    }
  };

  const setActionsPanelTab = (tab: DirectoryActionsPanelTab) => {
    actionsPanelTab.value = tab;
  };

  const updateDraftTitle = (title: string) => {
    if (!draftDetails.value) {
      return;
    }
    serverTitleError.value = '';
    draftDetails.value = { ...draftDetails.value, title };
  };

  const updateDraftIconId = (iconId: string) => {
    if (!draftDetails.value) {
      return;
    }
    draftDetails.value = {
      ...draftDetails.value,
      iconId: iconId as DirectoryConfigureDraft['iconId'],
    };
  };

  const updateDraftArchived = (archived: boolean) => {
    if (!draftDetails.value) {
      return;
    }
    draftDetails.value = { ...draftDetails.value, archived };
  };

  const togglePermissionEntry = (section: DirectoryPermissionSectionKey, entryId: string) => {
    if (!draftPermissions.value) {
      return;
    }
    const entries = getSectionEntries(draftPermissions.value, section).map((entry) =>
      entry.id === entryId ? { ...entry, allowed: !entry.allowed } : entry
    );
    draftPermissions.value = updateSectionEntries(draftPermissions.value, section, entries);
  };

  const togglePermissionSelectAll = (
    section: DirectoryPermissionSectionKey,
    entryIds?: string[]
  ) => {
    if (!draftPermissions.value) {
      return;
    }

    const current = getSectionEntries(draftPermissions.value, section);
    const targetIds = new Set(entryIds ?? current.map((entry) => entry.id));
    const visibleEntries = current.filter((entry) => targetIds.has(entry.id));

    if (visibleEntries.length === 0) {
      return;
    }

    const allSelected = visibleEntries.every((entry) => entry.allowed);
    const entries = current.map((entry) =>
      targetIds.has(entry.id) ? { ...entry, allowed: !allSelected } : entry
    );

    draftPermissions.value = updateSectionEntries(draftPermissions.value, section, entries);
  };

  const clearPermissionSection = (section: DirectoryPermissionSectionKey) => {
    if (!draftPermissions.value) {
      return;
    }
    const entries = getSectionEntries(draftPermissions.value, section).map((entry) => ({
      ...entry,
      allowed: false,
    }));
    draftPermissions.value = updateSectionEntries(draftPermissions.value, section, entries);
  };

  const persistPermissionsToTree = (nodeId: string) => {
    if (!draftPermissions.value) {
      return;
    }
    const permissions = clonePermissions(draftPermissions.value);
    directoryTree.value = updateNodeById(directoryTree.value, nodeId, (node) => ({
      ...node,
      permissions,
    }));
  };

  const persistPermissionsToSelection = () => {
    for (const nodeId of selectedNodeIds.value) {
      persistPermissionsToTree(nodeId);
    }
  };

  const resolveSelectionPermissionTargets = (): DirectoryPermissionsTarget[] | null => {
    const targets: DirectoryPermissionsTarget[] = [];

    for (const nodeId of selectedNodeIds.value) {
      const node = findNodeById(directoryTree.value, nodeId);
      const target = node ? resolvePermissionsTarget(node) : null;
      if (!target) {
        return null;
      }
      targets.push(target);
    }

    return targets.length > 0 ? targets : null;
  };

  const persistConfigureToTree = (nodeId: string) => {
    const draft = draftDetails.value;
    if (!draft) {
      return;
    }
    const title = draft.title.trim();
    directoryTree.value = updateNodeById(directoryTree.value, nodeId, (node) => ({
      ...node,
      name: title,
      details: {
        title,
        iconId: node.kind === 'module' ? draft.iconId : node.details?.iconId,
        archived: node.kind === 'matrix' ? draft.archived : node.details?.archived,
        createdAtLabel: node.details?.createdAtLabel ?? '-',
        createdBy: node.details?.createdBy ?? '-',
        savedInDirectoryBy: node.details?.savedInDirectoryBy ?? '-',
      },
    }));
  };

  const buildSectionPayload = (
    section: DirectoryPermissionSectionKey,
    entryIds: number[]
  ): BulkDirectoryPermissionSections =>
    section === 'positions' ? { positionIds: entryIds } : { functionalActivityIds: entryIds };

  const isSameSelection = (nodeIds: string[]) =>
    nodeIds.length === selectedNodeIds.value.length &&
    nodeIds.every((nodeId, index) => nodeId === selectedNodeIds.value[index]);

  const applyPermissionSection = async (
    section: DirectoryPermissionSectionKey,
    isSaving: Ref<boolean>,
    persist: (
      target: DirectoryPermissionsTarget,
      entryIds: number[]
    ) => Promise<UpdateDirectoryPermissionsResult>
  ) => {
    if (!draftPermissions.value) {
      return;
    }

    const targets = resolveSelectionPermissionTargets();
    const entryIds = getSectionEntries(draftPermissions.value, section)
      .filter((entry) => entry.allowed)
      .map((entry) => Number(entry.id));

    if (!targets || entryIds.some((id) => !Number.isInteger(id))) {
      toast.showErrorToast({ title: SAVE_PERMISSIONS_ERROR_MESSAGE, type: 'error' });
      return;
    }

    const selectionSnapshot = [...selectedNodeIds.value];
    const [firstTarget] = targets;

    isSaving.value = true;

    try {
      // Um alvo só continua indo pela rota do próprio item; a rota em lote existe para a seleção
      // múltipla, onde os itens precisam ser escritos na mesma transação.
      const result =
        targets.length === 1 && firstTarget
          ? await persist(firstTarget, entryIds)
          : await bulkUpdateDirectoryPermissions(targets, buildSectionPayload(section, entryIds));

      if (!result.success) {
        toast.showErrorToast({ title: SAVE_PERMISSIONS_ERROR_MESSAGE, type: 'error' });
        return;
      }

      // A seleção pode ter mudado enquanto a requisição estava em voo. Repintar o painel agora
      // mostraria as permissões destes itens sob outro item, e o próximo toggle as mandaria para o
      // alvo errado.
      if (isSameSelection(selectionSnapshot) && draftPermissions.value) {
        savedPermissions.value = clonePermissions(result.data);
        // Só a seção escrita adota o estado do servidor. A seção irmã mantém o que o usuário ainda
        // tem pendente, que a resposta não tem como conhecer.
        draftPermissions.value = updateSectionEntries(
          draftPermissions.value,
          section,
          getSectionEntries(result.data, section).map((entry) => ({ ...entry }))
        );
        persistPermissionsToSelection();
      }

      toast.showSuccessToast({ title: SAVE_PERMISSIONS_SUCCESS_MESSAGE, type: 'success' });
    } finally {
      isSaving.value = false;
    }
  };

  const applyPositions = async () => {
    if (!canApplyPositions.value) {
      return;
    }

    await applyPermissionSection(
      'positions',
      isSavingPositions,
      updateDirectoryPositionPermissions
    );
  };

  const applyFunctionalActivities = async () => {
    if (!canApplyFunctionalActivities.value) {
      return;
    }

    await applyPermissionSection(
      'functionalActivities',
      isSavingFunctionalActivities,
      updateDirectoryFunctionalActivityPermissions
    );
  };

  const toggleSuperadmin = () => {
    if (!draftPermissions.value) {
      return;
    }
    draftPermissions.value = {
      ...draftPermissions.value,
      superadmin: !draftPermissions.value.superadmin,
    };
  };

  const copyPermissions = () => {
    if (!draftPermissions.value) {
      return;
    }
    permissionsClipboard.copy({
      ...clonePermissions(draftPermissions.value),
      superadmin: false,
    });
    toast.showSuccessToast({
      title: COPY_PERMISSIONS_SUCCESS_MESSAGE,
      type: 'success',
    });
  };

  const canPastePermissions = computed(
    () => permissionsClipboard.hasPermissions && !isPastingPermissions.value
  );

  const getAllowedNumericIds = (
    permissions: DirectoryNodePermissions,
    section: DirectoryPermissionSectionKey
  ) =>
    getSectionEntries(permissions, section)
      .filter((entry) => entry.allowed)
      .map((entry) => Number(entry.id));

  const pastePermissions = async () => {
    const copied = permissionsClipboard.permissions;
    if (!copied || selectedNodeIds.value.length === 0) {
      return;
    }

    const targets = resolveSelectionPermissionTargets();
    const positionIds = getAllowedNumericIds(copied, 'positions');
    const functionalActivityIds = getAllowedNumericIds(copied, 'functionalActivities');

    if (
      !targets ||
      [...positionIds, ...functionalActivityIds].some((id) => !Number.isInteger(id))
    ) {
      toast.showErrorToast({ title: SAVE_PERMISSIONS_ERROR_MESSAGE, type: 'error' });
      return;
    }

    const selectionSnapshot = [...selectedNodeIds.value];

    isPastingPermissions.value = true;

    try {
      // Colar escreve as duas seções, então vai sempre pela rota em lote — inclusive com um alvo só,
      // para cargos e atividades funcionais caírem na mesma transação.
      const result = await bulkUpdateDirectoryPermissions(targets, {
        positionIds,
        functionalActivityIds,
      });

      if (!result.success) {
        toast.showErrorToast({ title: SAVE_PERMISSIONS_ERROR_MESSAGE, type: 'error' });
        return;
      }

      if (isSameSelection(selectionSnapshot)) {
        // O superadmin é do item de destino: não viaja na cópia nem é reescrito pelo colar.
        const superadmin = draftPermissions.value?.superadmin ?? result.data.superadmin;
        savedPermissions.value = clonePermissions(result.data);
        draftPermissions.value = { ...clonePermissions(result.data), superadmin };
        persistPermissionsToSelection();
      }

      toast.showSuccessToast({ title: SAVE_PERMISSIONS_SUCCESS_MESSAGE, type: 'success' });
    } finally {
      isPastingPermissions.value = false;
    }
  };

  const dispatchConfigureUpdate = (
    node: DirectoryTreeNode,
    draft: DirectoryConfigureDraft
  ): Promise<UpdateDirectoryNodeResult> | null => {
    const title = draft.title.trim();

    if (node.kind === 'module') {
      const moduleId = extractNumericId(node.id, 'module-');
      return moduleId === undefined
        ? null
        : updateModule(moduleId, { name: title, iconId: draft.iconId });
    }

    if (node.kind === 'folder') {
      const folderId = extractNumericId(node.id, 'folder-');
      return folderId === undefined ? null : updateFolder(folderId, { name: title });
    }

    if (node.kind === 'matrix') {
      const isArchived = draft.archived === true;
      if (buildConfigureDraft(node).archived === isArchived) {
        return Promise.resolve({ success: true });
      }
      return node.itemId === undefined
        ? null
        : updateDirectoryMatrixArchive(node.itemId, isArchived);
    }

    return null;
  };

  const saveConfigure = async (nodeId: string) => {
    const node = selectedNode.value;
    const draft = draftDetails.value;
    if (!node || !draft || !canSaveConfigure.value) {
      return;
    }

    isSavingConfigure.value = true;
    try {
      const request = dispatchConfigureUpdate(node, draft);
      if (!request) {
        toast.showErrorToast({ title: SAVE_CONFIGURE_ERROR_MESSAGE, type: 'error' });
        return;
      }

      const result = await request;
      if (!result.success) {
        if (result.duplicate) {
          serverTitleError.value =
            DIRECTORY_NODE_COPY[node.kind].duplicateTitleMessage ?? SAVE_CONFIGURE_ERROR_MESSAGE;
          return;
        }
        toast.showErrorToast({
          title: result.message ?? SAVE_CONFIGURE_ERROR_MESSAGE,
          type: 'error',
        });
        // Only a refusal with a reason resets the title: a failure with no answer keeps the typed
        // name so the save can be retried.
        const isRefused = result.message !== undefined;
        if (
          isRefused &&
          node.kind === 'folder' &&
          selectedNodeId.value === nodeId &&
          draftDetails.value
        ) {
          draftDetails.value = { ...draftDetails.value, title: buildConfigureDraft(node).title };
        }
        return;
      }

      persistConfigureToTree(nodeId);
      toast.showSuccessToast({ title: SAVE_CONFIGURE_SUCCESS_MESSAGE, type: 'success' });
    } finally {
      isSavingConfigure.value = false;
    }
  };

  const savePanel = () => {
    if (actionsPanelTab.value === 'permissions') {
      void applyPositions();
      return;
    }

    const nodeId = selectedNodeId.value;
    if (!nodeId) {
      return;
    }

    void saveConfigure(nodeId);
  };

  const handleAddChild = (): boolean => {
    if (!selectedNodeId.value) {
      return false;
    }
    createFolderTitleError.value = '';
    return true;
  };

  const confirmDeleteSelection = async (): Promise<boolean> => {
    if (
      isDeletingSelection.value ||
      selectedNodeIds.value.length === 0 ||
      deleteLockMessage.value !== null
    ) {
      return false;
    }

    isDeletingSelection.value = true;

    const nodeIds = [...selectedNodeIds.value];
    const nodes = nodeIds
      .map((nodeId) => findNodeById(directoryTree.value, nodeId))
      .filter((node): node is DirectoryTreeNode => node !== null);

    const itemType = resolveDeleteMessageItemType(nodes);

    const hasNodeWithoutValidOperation =
      nodes.length !== nodeIds.length ||
      nodes.some((node) => {
        if (node.kind === 'module') {
          return extractNumericId(node.id, 'module-') === undefined;
        }
        if (node.kind === 'folder') {
          return extractNumericId(node.id, 'folder-') === undefined;
        }
        return buildRemoveDirectoryItem(node) === null;
      });

    if (hasNodeWithoutValidOperation) {
      toast.showErrorToast({ title: buildDeleteErrorMessage(itemType), type: 'error' });
      isDeletingSelection.value = false;
      return false;
    }

    const moduleIds = nodes
      .filter((node) => node.kind === 'module')
      .map((node) => extractNumericId(node.id, 'module-'))
      .filter((id): id is number => id !== undefined);

    const folderIds = nodes
      .filter((node) => node.kind === 'folder')
      .map((node) => extractNumericId(node.id, 'folder-'))
      .filter((id): id is number => id !== undefined);

    const itemsToRemove = nodes
      .map((node) => buildRemoveDirectoryItem(node))
      .filter((item): item is RemoveDirectoryItem => item !== null);

    try {
      const results = await Promise.all([
        ...moduleIds.map((id) => deleteModule(id)),
        ...folderIds.map((id) => deleteFolder(id)),
        ...(itemsToRemove.length > 0 ? [removeDirectoryItems(itemsToRemove)] : []),
      ]);

      const failed = results.find((result) => !result.success);
      if (failed) {
        throw new Error(failed.message ?? buildDeleteErrorMessage(itemType));
      }

      let nextTree = directoryTree.value;
      for (const nodeId of nodeIds) {
        nextTree = removeNodeById(nextTree, nodeId).tree;
      }
      directoryTree.value = nextTree;
      selectedNodeId.value = '';
      selectedNodeIds.value = [];

      toast.showSuccessToast({ title: buildDeleteSuccessMessage(itemType), type: 'success' });
      return true;
    } catch (error) {
      const title =
        error instanceof Error && error.message ? error.message : buildDeleteErrorMessage(itemType);
      toast.showErrorToast({ title, type: 'error' });
      return false;
    } finally {
      isDeletingSelection.value = false;
    }
  };

  const appendModuleToTree = (node: DirectoryTreeNode) => {
    directoryTree.value = [...directoryTree.value, attachMockChildrenToModule(node)];
  };

  const loadModules = async () => {
    const requestId = ++loadModulesRequestId;
    isLoadingModules.value = true;
    try {
      const sortField = isModuleTreeSortField(sort.value) ? sort.value : undefined;
      const sortOrder = isModuleTreeSortOrder(order.value) ? order.value : undefined;
      const modules = await getModulesWithFolders(sortField, sortOrder);
      if (requestId !== loadModulesRequestId) {
        return;
      }
      if (modules.length === 0) {
        directoryTree.value = [];
        selectedNodeId.value = '';
        return;
      }
      directoryTree.value = modules.map((module) =>
        mapModuleWithFoldersToTreeNode(module, sortField, sortOrder)
      );
    } catch {
      if (requestId !== loadModulesRequestId) {
        return;
      }
      toast.showErrorToast({
        title: LOAD_MODULES_ERROR_MESSAGE,
        type: 'error',
      });
    } finally {
      if (requestId === loadModulesRequestId) {
        isLoadingModules.value = false;
      }
    }
  };

  if (!usesProvidedTree) {
    onMounted(() => {
      void loadModules();
    });
  }

  const handleCreateModuleSubmit = async (payload: CreateModuleSubmitPayload): Promise<boolean> => {
    if (isCreateModuleSubmitting.value) {
      return false;
    }

    isCreateModuleSubmitting.value = true;
    createModuleTitleError.value = '';

    const result = await createModule({
      name: payload.name,
      iconId: payload.iconId,
    });

    isCreateModuleSubmitting.value = false;

    if (result.success) {
      if (isModuleTreeSortField(sort.value) && !usesProvidedTree) {
        void loadModules();
      } else {
        appendModuleToTree(mapCreatedModuleToTreeNode(result.data, userStore.name ?? '-'));
      }
      toast.showSuccessToast({
        title: CREATE_MODULE_SUCCESS_MESSAGE,
        type: 'success',
      });
      return true;
    }

    if (result.duplicate) {
      createModuleTitleError.value = MODULE_ALREADY_EXISTS_MESSAGE;
      return false;
    }

    toast.showErrorToast({
      title: CREATE_MODULE_ERROR_MESSAGE,
      type: 'error',
    });
    return false;
  };

  const handleCreateFolderSubmit = async (payload: CreateFolderSubmitPayload): Promise<boolean> => {
    if (isCreateFolderSubmitting.value) {
      return false;
    }

    const parentNode = selectedNode.value;
    if (!parentNode) {
      return false;
    }

    const target = buildCreateFolderTarget(parentNode);
    if (!target) {
      toast.showErrorToast({
        title: CREATE_FOLDER_ERROR_MESSAGE,
        type: 'error',
      });
      return false;
    }

    isCreateFolderSubmitting.value = true;
    createFolderTitleError.value = '';

    const result = await createFolder({
      moduleId: target.moduleId,
      parentId: target.parentId,
      name: payload.name,
    });

    isCreateFolderSubmitting.value = false;

    if (result.success) {
      if (isModuleTreeSortField(sort.value) && !usesProvidedTree) {
        void loadModules();
      } else {
        directoryTree.value = appendChildToTree(
          directoryTree.value,
          parentNode.id,
          mapCreatedFolderToTreeNode(result.data, userStore.name ?? '-')
        );
      }
      toast.showSuccessToast({
        title: CREATE_FOLDER_SUCCESS_MESSAGE,
        type: 'success',
      });
      return true;
    }

    if (result.duplicate) {
      createFolderTitleError.value = FOLDER_ALREADY_EXISTS_MESSAGE;
      return false;
    }

    toast.showErrorToast({
      title: CREATE_FOLDER_ERROR_MESSAGE,
      type: 'error',
    });
    return false;
  };

  const handleAddRecordSubmit = async (selections: RecordSelection[]): Promise<boolean> => {
    if (isAddRecordSubmitting.value) {
      return false;
    }

    const folderNode = selectedNode.value;
    if (!folderNode) {
      return false;
    }

    const container = resolveRecordLinkContainer(folderNode);

    if (!container) {
      toast.showErrorToast({
        title: ADD_RECORD_ERROR_MESSAGE,
        type: 'error',
      });
      return false;
    }

    const matrixIds = selections.map(({ revision }) => revision.id);

    isAddRecordSubmitting.value = true;
    try {
      await linkMatricesToContainer(container, matrixIds);
    } catch (error) {
      toast.showErrorToast({
        ...resolveMatrixLinkErrorToast(error, selections),
        type: 'error',
      });
      return false;
    } finally {
      isAddRecordSubmitting.value = false;
    }

    // The matrix lands under "Registros da Qualidade", not in the selected folder, so only the
    // server's tree knows where it went.
    await loadModules();

    toast.showSuccessToast({
      title: ADD_RECORD_SUCCESS_MESSAGE,
      type: 'success',
    });
    return true;
  };

  const handleAddDocumentSubmit = async (revisions: DocumentRevision[]): Promise<boolean> => {
    if (isAddDocumentSubmitting.value) {
      return false;
    }

    const folderNode = selectedNode.value;
    if (!folderNode) {
      return false;
    }

    const container = resolveDocumentLinkContainer(folderNode);

    if (!container) {
      toast.showErrorToast({
        title: ADD_DOCUMENT_ERROR_MESSAGE,
        type: 'error',
      });
      return false;
    }

    // A revisão é a unidade do vínculo: duas revisões do mesmo documento são dois itens, e não um
    // duplicado. O dedupe é por revisão, só para a rede de segurança do contrato do endpoint, que
    // rejeita o lote inteiro (400) em id repetido.
    const uniqueRevisions = [
      ...new Map(revisions.map((revision) => [revision.id, revision])).values(),
    ];

    isAddDocumentSubmitting.value = true;
    try {
      await linkDocumentRevisionsToContainer(
        container,
        uniqueRevisions.map((revision) => revision.id)
      );
    } catch (error) {
      toast.showErrorToast({
        title: isDocumentAlreadyLinkedError(error)
          ? ADD_DOCUMENT_DUPLICATE_MESSAGE
          : ADD_DOCUMENT_ERROR_MESSAGE,
        type: 'error',
      });
      return false;
    } finally {
      isAddDocumentSubmitting.value = false;
    }

    if (usesProvidedTree) {
      directoryTree.value = uniqueRevisions.reduce(
        (tree, revision) =>
          appendChildToTree(
            tree,
            folderNode.id,
            mapDocumentRevisionToTreeNode(revision, folderNode.id)
          ),
        directoryTree.value
      );
    } else {
      await loadModules();
    }

    toast.showSuccessToast({
      title: ADD_DOCUMENT_SUCCESS_MESSAGE,
      type: 'success',
    });
    return true;
  };

  return {
    searchTerm,
    searchCriterion,
    currentSort,
    currentOrder,
    expandedIds,
    filteredTree,
    shouldShowSearchNoResults,
    selectedNodeId,
    selectedNodeIds,
    selectedNode,
    selectedNodeDetails,
    selectedNodePermissions,
    isLoadingPermissions,
    isConfigureDirty,
    isConfigureTitleValid,
    isConfigureIconValid,
    isSavingConfigure,
    configureTitleError,
    canSaveConfigure,
    actionsPanelTab,
    hasModules,
    isLoadingModules,
    canCreateModule,
    canViewPermissionsTab,
    canToggleSuperadmin,
    getDirectoryKey,
    getDirectoryChildren,
    selectNode,
    moveSelectedNodes,
    dragOverNodeId: dragAndDrop.dragOverNodeId,
    onNodeDragStart: dragAndDrop.onNodeDragStart,
    onNodeDragOver: dragAndDrop.onNodeDragOver,
    onNodeDragLeave: dragAndDrop.onNodeDragLeave,
    onNodeDrop: dragAndDrop.onNodeDrop,
    onNodeDragEnd: dragAndDrop.onNodeDragEnd,
    handleSearch,
    handleSortUpdate,
    setActionsPanelTab,
    updateDraftTitle,
    updateDraftIconId,
    updateDraftArchived,
    toggleSuperadmin,
    togglePermissionPosition: (id: string) => togglePermissionEntry('positions', id),
    togglePermissionFunctionalActivity: (id: string) =>
      togglePermissionEntry('functionalActivities', id),
    togglePositionsSelectAll: (entryIds?: string[]) =>
      togglePermissionSelectAll('positions', entryIds),
    toggleFunctionalActivitiesSelectAll: (entryIds?: string[]) =>
      togglePermissionSelectAll('functionalActivities', entryIds),
    applyPositions,
    applyFunctionalActivities,
    clearPositions: () => clearPermissionSection('positions'),
    clearFunctionalActivities: () => clearPermissionSection('functionalActivities'),
    canApplyPositions,
    canClearPositions,
    canApplyFunctionalActivities,
    canClearFunctionalActivities,
    isSavingPositions,
    isSavingFunctionalActivities,
    copyPermissions,
    pastePermissions,
    canPastePermissions,
    isPastingPermissions,
    savePanel,
    handleAddChild,
    deleteSelectionState,
    selectedCount,
    isDeleteButtonVisible,
    deleteLockMessage,
    deleteModalTitle,
    deleteModalDescription,
    isDeletingSelection,
    confirmDeleteSelection,
    isCreateModuleSubmitting,
    createModuleTitleError,
    handleCreateModuleSubmit,
    isCreateFolderSubmitting,
    createFolderTitleError,
    handleCreateFolderSubmit,
    isAddRecordSubmitting,
    handleAddRecordSubmit,
    isAddDocumentSubmitting,
    handleAddDocumentSubmit,
    eligibleDocumentsSearchUrl,
    eligibleMatricesSearchUrl,
  };
}
